import React, { useState, useEffect, useRef } from 'react';

const IOSTimePicker = ({ initialHour = 9, initialPeriod = 'AM', onChange }) => {
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const periods = ['AM', 'PM'];

  const [selectedHour, setSelectedHour] = useState(initialHour);
  const [selectedPeriod, setSelectedPeriod] = useState(initialPeriod);

  const hourRef = useRef(null);
  const periodRef = useRef(null);

  const hourScrollTimeout = useRef(null);
  const periodScrollTimeout = useRef(null);

  const isProgrammaticScroll = useRef(false);

  const getItemHeight = () => 36;

  const enableDragScroll = columnRef => {
    const el = columnRef.current;
    if (!el || window.innerWidth < 768) return;

    let isDown = false;
    let startY;
    let scrollTop;

    const onMouseDown = e => {
      if (e.button !== 0) return;
      isDown = true;
      startY = e.pageY;
      scrollTop = el.scrollTop;
    };

    const onMouseUp = () => (isDown = false);

    const onMouseMove = e => {
      if (!isDown) return;
      e.preventDefault();
      const walk = e.pageY - startY;
      el.scrollTop = scrollTop - walk;
    };

    el.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('mousemove', onMouseMove);

    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('mousemove', onMouseMove);
    };
  };

  useEffect(() => {
    const cleanups = [enableDragScroll(hourRef), enableDragScroll(periodRef)];

    return () => cleanups.forEach(fn => fn && fn());
  }, []);

  const handleScroll = (ref, values, setValue, currentValue, timeoutRef) => {
    if (isProgrammaticScroll.current) return;
    if (!ref.current) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      if (!ref.current) return;
      const scrollTop = ref.current.scrollTop;
      const itemHeight = getItemHeight();

      const index = Math.round(scrollTop / itemHeight);
      const newValue = values[index];

      const targetScrollTop = index * itemHeight;

      if (ref.current.scrollTop !== targetScrollTop) {
        ref.current.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth',
        });
      }

      if (newValue !== undefined && newValue !== currentValue) {
        setValue(newValue);
      }
    }, 150);
  };

  const scrollToValue = (ref, values, value, behavior = 'smooth') => {
    if (!ref.current) return;
    const index = values.indexOf(value);
    if (index < 0) return;

    const itemHeight = getItemHeight();
    const targetScrollTop = index * itemHeight;

    ref.current.scrollTo({
      top: targetScrollTop,
      behavior,
    });
  };

  useEffect(() => {
    isProgrammaticScroll.current = true;

    if (initialHour !== selectedHour) {
      setSelectedHour(initialHour);
    }

    if (initialPeriod !== selectedPeriod) {
      setSelectedPeriod(initialPeriod);
    }

    const timeout = setTimeout(() => {
      scrollToValue(hourRef, hours, initialHour, 'auto');
      scrollToValue(periodRef, periods, initialPeriod, 'auto');

      setTimeout(() => {
        isProgrammaticScroll.current = false;
      }, 100);
    }, 80);

    return () => clearTimeout(timeout);
  }, [initialHour, initialPeriod]);

  useEffect(() => {
    if (onChange && !isProgrammaticScroll.current) {
      onChange({
        hour: selectedHour,
        minute: '00',
        period: selectedPeriod,
      });
    }
  }, [selectedHour, selectedPeriod, onChange]);

  return (
    <div className="picker-container">
      <div
        ref={hourRef}
        className="picker-column"
        onScroll={() =>
          handleScroll(
            hourRef,
            hours,
            setSelectedHour,
            selectedHour,
            hourScrollTimeout,
          )
        }
      >
        {hours.map(h => (
          <div
            key={h}
            className={`picker-item ${h === selectedHour ? 'selected' : ''}`}
          >
            {h}:00
          </div>
        ))}
      </div>

      <div
        ref={periodRef}
        className="picker-column"
        onScroll={() =>
          handleScroll(
            periodRef,
            periods,
            setSelectedPeriod,
            selectedPeriod,
            periodScrollTimeout,
          )
        }
      >
        {periods.map(p => (
          <div
            key={p}
            className={`picker-item ${p === selectedPeriod ? 'selected' : ''}`}
          >
            {p}
          </div>
        ))}
      </div>
    </div>
  );
};

export default IOSTimePicker;
